import CaptainAssistantMcpServers from 'dashboard/api/captain/assistantMcpServer';

// Custom store rather than createStore-factory because the records are
// scoped per-assistant and the underlying API is doubly-nested. Keeps
// state.records keyed by attachment id but also exposes a getter that
// derives "is this server attached to the active assistant?" so the UI
// can render toggle states efficiently.
const state = {
  records: [], // Array of { id, captain_mcp_server_id, enabled_tools, mcp_server: {...} }
  currentAssistantId: null,
  // Monotonically incrementing token used to discard stale fetch
  // responses. When the user navigates from assistant A to assistant B
  // before A's request resolves, A's response would otherwise overwrite
  // B's records with the wrong attachments — see fetch action below.
  inflightToken: 0,
  uiFlags: {
    fetching: false,
    saving: false,
  },
};

const getters = {
  records: $state => $state.records,
  uiFlags: $state => $state.uiFlags,
  // Map from MCP server id -> attachment record. Empty when nothing is
  // attached or before fetch resolves.
  byServerId: $state => {
    const map = {};
    $state.records.forEach(r => {
      map[r.captain_mcp_server_id] = r;
    });
    return map;
  },
};

const mutations = {
  SET_FETCHING($state, value) {
    $state.uiFlags.fetching = value;
  },
  SET_SAVING($state, value) {
    $state.uiFlags.saving = value;
  },
  // Bump on every fetch start. Returned to the caller so it can
  // recognise its own response when it resolves and ignore stale ones.
  BEGIN_FETCH($state, assistantId) {
    $state.inflightToken += 1;
    $state.currentAssistantId = assistantId;
  },
  SET_RECORDS($state, { assistantId, records }) {
    $state.currentAssistantId = assistantId;
    $state.records = records;
  },
  UPSERT($state, record) {
    const idx = $state.records.findIndex(r => r.id === record.id);
    if (idx >= 0) $state.records.splice(idx, 1, record);
    else $state.records.push(record);
  },
  REMOVE_BY_SERVER($state, serverId) {
    $state.records = $state.records.filter(
      r => r.captain_mcp_server_id !== serverId
    );
  },
};

const actions = {
  // Race-safe fetch. We bump an inflight token before issuing the
  // request and snapshot it locally; when the request resolves we only
  // commit if the snapshot still matches state.inflightToken AND the
  // assistant id matches. This drops responses for an assistant the
  // user has already navigated away from, so a slow A response cannot
  // overwrite a fast B response.
  async fetch({ commit, state: $state }, assistantId) {
    commit('BEGIN_FETCH', assistantId);
    const myToken = $state.inflightToken;
    commit('SET_FETCHING', true);
    try {
      const { data } = await CaptainAssistantMcpServers.index(assistantId);
      const stillCurrent =
        $state.inflightToken === myToken &&
        $state.currentAssistantId === assistantId;
      if (stillCurrent) {
        commit('SET_RECORDS', { assistantId, records: data.payload || [] });
      }
    } finally {
      // Only clear the spinner if our response is still the current
      // one; otherwise a newer in-flight fetch is still loading.
      if ($state.inflightToken === myToken) commit('SET_FETCHING', false);
    }
  },

  async attach({ commit }, { assistantId, serverId, enabledTools = null }) {
    commit('SET_SAVING', true);
    try {
      const { data } = await CaptainAssistantMcpServers.attach(assistantId, {
        serverId,
        enabledTools,
      });
      commit('UPSERT', data);
      return data;
    } finally {
      commit('SET_SAVING', false);
    }
  },

  async updateEnabledTools(
    { commit },
    { assistantId, serverId, enabledTools }
  ) {
    commit('SET_SAVING', true);
    try {
      const { data } = await CaptainAssistantMcpServers.updateEnabledTools(
        assistantId,
        serverId,
        enabledTools
      );
      commit('UPSERT', data);
      return data;
    } finally {
      commit('SET_SAVING', false);
    }
  },

  async detach({ commit }, { assistantId, serverId }) {
    commit('SET_SAVING', true);
    try {
      await CaptainAssistantMcpServers.detach(assistantId, serverId);
      commit('REMOVE_BY_SERVER', serverId);
    } finally {
      commit('SET_SAVING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
